// 배차(적재) 계산기 — 공용 기본값으로 시작해 화면에서 계산한다.
// 값을 고치면 내 브라우저에 사용자별로 저장한다(pages/dispatch/dispatchStore) — 새로고침해도 유지되고,
// «기본값으로 되돌리기»·«새 기본값 적용»을 누르면 지운다. 안 고친 사용자는 저장값이 없어 새 공용 기본값을 그대로 받는다.
// 고친 사용자는 고칠 때 보던 공용 기본값(지문)이 지금과 다르면 배너를 본다.
// 계산은 pages/dispatch/dispatchCalc.js(순수 함수) · 공용 기본값 저장은 DispatchDefaultsPanel(서버 can_edit 일 때만 버튼).
// 메뉴 노출·URL 가드는 utils/menuAccess · 실제 차단은 서버(require_menu("dispatch-calc")).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconCalculator } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { Page, PageHeader, PillTabs, Button } from '../components/ui';
import { dispatchCalcAPI } from '../api/client';
import { toast } from '../components/AppDialog';
import { copyToClipboard } from '../utils/clipboard';
import { fmtKstDateTime } from '../utils/kstTime';
import { COLORS, badge, card, input } from '../utils/uiStyles';
import DispatchDefaultsPanel from './dispatch/DispatchDefaultsPanel';
import {
  DIM_KEYS, MODE_LABELS, altSack, altSackText, applyInput, applyMix, applySeason, compute, defaultsFingerprint,
  fmtInt, fmtNum, fmtRatio, fmtTrucks, fmtVol1, fmtVol2, matchMix, matchSeason, resolveCounts, scenarios, setMode,
  shareText, stateFromDefaults, stateFromSaved, subtitle,
} from './dispatch/dispatchCalc';
import { clearSaved, currentUid, readSaved, writeSaved } from './dispatch/dispatchStore';

// 시안 F 색 — var(--gw-…, <라이트 hex>)(components/ui 와 같은 방식). 이 화면이 손대지 않은 카드·표 행은 COLORS 그대로.
const V = {
  text: `var(--gw-text, ${COLORS.text})`,
  secondary: `var(--gw-secondary, ${COLORS.textSub})`,
  muted: `var(--gw-muted, ${COLORS.textMuted})`,
  line: `var(--gw-line, ${COLORS.borderSoft})`,
  graySoft: 'var(--gw-gray-soft, #f1f1ef)',
};
// 결과 10칸 — 공용 .gw-metrics 는 4열이라 5열×2줄로 선을 바로잡고, 묶음 폭 880 이하(컨테이너 질의 · 확대·터치·사이드바 무관)는 2열×5줄.
// ≤600 칩 = 입력 방식 탭과 같은 글자·패딩(index.html 의 .gw-tab 규칙).
const PAGE_CSS = `.dc-metrics>.gw-metric:nth-child(5n){border-right:0}.dc-metrics>.gw-metric:nth-child(-n+5){border-bottom:1px solid var(--gw-line,${COLORS.borderSoft})}`
  + `@container dc-metrics (max-width:880px){.dc-metrics{grid-template-columns:repeat(2,minmax(0,1fr))!important}.dc-metrics>.gw-metric{border-right:1px solid var(--gw-line,${COLORS.borderSoft})!important;border-bottom:1px solid var(--gw-line,${COLORS.borderSoft})!important}.dc-metrics>.gw-metric:nth-child(2n){border-right:0!important}.dc-metrics>.gw-metric:nth-last-child(-n+2){border-bottom:0!important}}`
  + '@media (max-width:600px){.dc-chip{padding:5px 8px!important;font-size:12px!important}}';
const th = { height: '33px', padding: '5px 8px', textAlign: 'right', fontSize: '12px', fontWeight: 400, color: V.muted, whiteSpace: 'nowrap', borderBottom: `1px solid ${V.line}` };
const td = { padding: '6px 8px', textAlign: 'right', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap', verticalAlign: 'top' };
const sectionTitle = { margin: '0 0 10px', fontSize: '14px', fontWeight: 600, color: COLORS.text };
const errText = { color: COLORS.dangerText, fontSize: '11px', marginTop: '3px', whiteSpace: 'normal' };
const noteText = { color: COLORS.textSub, fontSize: '11px', marginTop: '3px', whiteSpace: 'normal' };
// 위쪽 입력 칸 — 라벨 13px · 칸 높이 31(Button md 와 같은 줄) · 표 안 숫자 칸(cellInput)은 표 행과 묶여 그대로.
const fieldLabel = { fontSize: '13px', color: V.secondary };
const fieldBox = { height: '31px', padding: '0 10px', fontSize: '13px' };
const DIM_LABELS = { width_cm: '가로(cm)', length_cm: '세로(cm)', height_cm: '높이(cm)' };
const COMMON_FIELDS = [
  ['common.per_sack', '마대당 파우치(개)'],
  ['common.days', '출고 일수(일)'],
  ['common.safety_pct', '안전 여유율(%)'],
  ['common.sacks_per_truck', '11톤 1대당 마대(개)'],
  ['common.truck_m3', '적재함 부피(㎥)'],
  ['common.load_pct', '차량 적재율(%)'],
];
const ROLE_GUIDE = '공용 기본값은 권한자만 바꿀 수 있고, 내가 바꾼 값은 내 브라우저에만 저장됩니다.';
const CHANGED_BANNER = '관리자가 공용 기본값을 바꿨습니다. 지금 화면은 내가 수정한 값입니다.';
const PRESET_GUIDE = '박스 구성 프리셋은 1~5호 구성비입니다. 비율 입력에서는 구성비를 바꾸고, '
  + '호수별 수량 입력에서는 지금 박스 수량 합계를 그 구성비로 다시 나눕니다.';
const idOf = (path) => `dc-${path.replace(/\./g, '-')}`;
const isHundred = (v) => Math.round(v * 1e6) / 1e6 === 100;

export default function DispatchCalcPage() {
  const [status, setStatus] = useState('loading');   // loading | ready | forbidden | error
  const [server, setServer] = useState(null);         // GET /defaults 응답
  const [calc, setCalc] = useState(null);             // 계산기 상태(dispatchCalc.stateFromDefaults)
  const [panelOpen, setPanelOpen] = useState(false);
  const [uid] = useState(() => currentUid());         // 저장 키의 사용자 — 화면을 연 사용자로 고정한다
  const [dirty, setDirty] = useState(false);          // 사용자가 값을 고쳤나(고쳤을 때만 브라우저에 저장한다)
  const [baseFp, setBaseFp] = useState('');           // 고칠 때 보던 공용 기본값 지문
  const [shareFallback, setShareFallback] = useState(null);   // 복사 실패 → 공유 문구를 선택해 보인다
  const mountedRef = useRef(false);
  const shareRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // 처음·다시 시도 — 브라우저 저장값이 있으면 그 값으로, 없으면 공용 기본값으로 시작한다.
  const loadAll = useCallback(async () => {
    setStatus('loading');
    try {
      const r = await dispatchCalcAPI.defaults();
      if (!mountedRef.current) return;
      const saved = readSaved(uid);
      setServer(r.data);
      if (saved) {
        setCalc(stateFromSaved(saved, r.data.defaults));
        setDirty(true);
        setBaseFp(saved.base);
      } else {
        setCalc(stateFromDefaults(r.data.defaults));
        setDirty(false);
        setBaseFp(defaultsFingerprint(r.data.defaults));
      }
      setStatus('ready');
    } catch (e) {
      if (!mountedRef.current) return;
      setStatus(e?.response?.status === 403 ? 'forbidden' : 'error');
    }
  }, [uid]);

  // 기본값 저장 뒤 — 머리 줄·프리셋만 새로 읽는다(계산 중인 값은 그대로 둔다).
  // 지금 화면 값을 저장했으면(screenSaved) 화면 = 새 공용 기본값이라 저장값을 지운다(본인에게 배너가 뜨지 않는다).
  const reloadServer = useCallback(async (opts) => {
    try {
      const r = await dispatchCalcAPI.defaults();
      if (!mountedRef.current) return;
      setServer(r.data);
      if (opts && opts.screenSaved) {
        setBaseFp(defaultsFingerprint(r.data.defaults));
        setDirty(false);
        clearSaved(uid);
      }
    } catch (e) {
      toast('기본값을 다시 불러오지 못했습니다.', 'error');
    }
  }, [uid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // 고친 값만 저장한다(안 고쳤으면 쓰지 않는다).
  useEffect(() => {
    if (!dirty || !calc) return;
    writeSaved(uid, { base: baseFp, mode: calc.mode, raw: calc.raw });
  }, [dirty, calc, baseFp, uid]);

  // 복사 실패 — 공유 문구 칸을 골라 선택해 둔다(사용자가 Ctrl+C 로 복사한다).
  useEffect(() => {
    if (!shareFallback || !shareRef.current) return;
    shareRef.current.focus();
    shareRef.current.select();
  }, [shareFallback]);

  const presets = server?.defaults?.presets;
  const view = useMemo(() => {
    if (!calc || !presets) return null;
    const counts = resolveCounts(calc.mode, calc.eff);
    const result = compute(counts, calc.eff);
    return { counts, result, alt: altSack(counts, result), rows: scenarios(calc.eff, calc.mode, presets) };
  }, [calc, presets]);

  const edit = (fn) => {
    setCalc(fn);
    setDirty(true);
  };
  const onInput = (path, raw) => edit((s) => applyInput(s, path, raw));
  // 기본값으로 되돌리기 · 새 기본값 적용 — 지금 공용 기본값으로 다시 시작하고 저장값을 지운다.
  const resetToDefaults = () => {
    setCalc(stateFromDefaults(server.defaults));
    setDirty(false);
    setBaseFp(defaultsFingerprint(server.defaults));
    clearSaved(uid);
  };
  const copyShare = async () => {
    const text = shareText(calc, presets);
    try {
      await copyToClipboard(text);
      setShareFallback(null);
      toast('복사됨');
    } catch (e) {
      setShareFallback({ at: Date.now() });   // 매번 새 객체 — 다시 실패해도 다시 선택한다
    }
  };

  // 칸 아래 안내 — 오류(빨강)가 먼저고, 오류가 없을 때만 보정 안내(회색).
  const fieldMessage = (path) => {
    const err = calc.errors[path];
    const note = err ? '' : (calc.notes?.[path] || '');
    let describedBy;
    if (err) describedBy = `${idOf(path)}-err`;
    else if (note) describedBy = `${idOf(path)}-note`;
    return { err, note, describedBy };
  };

  // 계산기 칸 하나(이름은 aria-label 로 — 기본값 편집 칸은 '기본값 · ' 이 붙어 겹치지 않는다).
  const field = (path, label, width = '120px') => {
    const { err, note, describedBy } = fieldMessage(path);
    return (
      <div key={path} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={fieldLabel}>{label}</span>
        <input id={idOf(path)} type="text" inputMode="decimal" aria-label={label} aria-invalid={err ? 'true' : 'false'}
          aria-describedby={describedBy} value={calc.raw[path]}
          onChange={(e) => onInput(path, e.target.value)}
          style={{ ...input, ...fieldBox, width, borderColor: err ? COLORS.danger : COLORS.inputBorder }} />
        {err && <span id={`${idOf(path)}-err`} style={errText}>{err}</span>}
        {note && <span id={`${idOf(path)}-note`} style={noteText}>{note}</span>}
      </div>
    );
  };
  const cellInput = (path, label) => {
    const { err, note, describedBy } = fieldMessage(path);
    return (
      <>
        <input id={idOf(path)} type="text" inputMode="decimal" aria-label={label} aria-invalid={err ? 'true' : 'false'}
          aria-describedby={describedBy} value={calc.raw[path]}
          onChange={(e) => onInput(path, e.target.value)}
          style={{ ...input, width: '72px', padding: '4px 6px', fontSize: '13px', textAlign: 'right', borderColor: err ? COLORS.danger : COLORS.inputBorder }} />
        {err && <div id={`${idOf(path)}-err`} style={errText}>{err}</div>}
        {note && <div id={`${idOf(path)}-note`} style={noteText}>{note}</div>}
      </>
    );
  };

  const header = () => {
    const info = server.is_factory
      ? '공용 기본값: 기본 제공값(수정 이력 없음)'
      : `공용 기본값: ${fmtKstDateTime(server.updated_at) || '—'} 수정 · ${server.updated_by_name || '—'}`;
    // 머리(PageHeader) 아래 한 줄 — 단추를 머리 actions 에 넣으면 601~768(확대 1.5배)에서 h1 이 세로로 쌓인다.
    // 역할 안내(dc-role-guide)는 머리 부제로 옮겼다.
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <span data-testid="dc-role-badge" style={badge(server.can_edit ? 'default' : 'success')}>
          {server.can_edit ? '기본값 관리 가능' : '사용자'}
        </span>
        <span data-testid="dc-defaults-info" style={{ fontSize: '12px', color: V.muted }}>{info}</span>
        <Button variant="secondary" onClick={resetToDefaults}>기본값으로 되돌리기</Button>
        {server.can_edit && (
          <Button variant="accent" onClick={() => setPanelOpen(true)} aria-expanded={panelOpen}>기본값 관리</Button>
        )}
      </div>
    );
  };

  const changedBanner = () => {
    if (!dirty || baseFp === defaultsFingerprint(server.defaults)) return null;
    return (
      <div data-testid="dc-defaults-changed" role="status"
        style={{ ...card, marginBottom: '12px', background: COLORS.warnSoft, borderColor: COLORS.warnSoft, color: COLORS.warn, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px' }}>{CHANGED_BANNER}</span>
        <Button variant="secondary" onClick={resetToDefaults}>새 기본값 적용</Button>
      </div>
    );
  };

  const presetBar = () => {
    const season = matchSeason(calc.eff, presets);
    const mix = matchMix(calc.eff, presets, calc.mode);
    // 알약 칩(PillTabs 단추와 같은 모양) — 🔴 gw-tab 클래스는 달지 않는다(호버 규칙이 aria-selected 만 봐서 눌린 칩 바탕을 덮는다).
    const chip = (pressed, onClick, text, title) => (
      <button type="button" className="dc-chip" aria-pressed={pressed} onClick={onClick} title={title}
        style={{
          display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: '18px',
          fontSize: '13px', border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
          background: pressed ? V.graySoft : 'transparent',
          color: pressed ? V.text : V.muted,
          fontWeight: pressed ? 500 : 400,
        }}>{text}</button>
    );
    const s = presets.season;
    const m = presets.mix;
    return (
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div role="group" aria-label="계절 프리셋" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: V.secondary }}>계절</span>
            {chip(season === 'current', () => edit((st) => applySeason(st, presets, 'current')), '현재',
              `파우치 ${fmtNum(s.current.pouch_pct)}% · 마대당 ${fmtNum(s.current.per_sack)}개`)}
            {chip(season === 'winter', () => edit((st) => applySeason(st, presets, 'winter')), '동절기',
              `파우치 ${fmtNum(s.winter.pouch_pct)}% · 마대당 ${fmtNum(s.winter.per_sack)}개`)}
          </div>
          <div role="group" aria-label="박스 구성 프리셋" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: V.secondary }}>박스 구성</span>
            {chip(mix === 'current', () => edit((st) => applyMix(st, presets, 'current')), '현재 (3호 중심)', m.current.map(fmtNum).join('/'))}
            {chip(mix === 'hanger', () => edit((st) => applyMix(st, presets, 'hanger')), '행거 시즌 (4·5호 중심)', m.hanger.map(fmtNum).join('/'))}
          </div>
        </div>
        <div data-testid="dc-preset-guide" style={{ fontSize: '11px', color: COLORS.textSub, marginTop: '6px' }}>{PRESET_GUIDE}</div>
      </div>
    );
  };

  const inputs = () => (
    <section aria-label="입력" style={{ ...card, marginBottom: '12px' }}>
      <PillTabs ariaLabel="입력 방식" idBase="dc-mode" value={calc.mode}
        onChange={(m) => { if (calc.mode !== m) edit((s) => setMode(s, m)); }}
        items={['ratio', 'qty'].map((m) => ({ key: m, label: MODE_LABELS[m] }))} />
      <div role="tabpanel" id={`dc-mode-panel-${calc.mode}`} aria-labelledby={`dc-mode-tab-${calc.mode}`}>
        {presetBar()}
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {calc.mode === 'ratio' ? (
            <>
              {field('ratio.total', '총 출고 물량(건)', '140px')}
              {field('ratio.pouch_pct', '파우치 비율(%)')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={fieldLabel}>박스 비율(%)</span>
                <input type="text" aria-label="박스 비율(%)" readOnly value={fmtRatio(view.counts.boxPct)}
                  style={{ ...input, ...fieldBox, width: '120px', background: COLORS.bg, color: COLORS.textSub }} />
              </div>
            </>
          ) : (
            field('qty.pouch', '파우치 수량(건)', '140px')
          )}
        </div>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          {COMMON_FIELDS.map(([path, label]) => field(path, label))}
        </div>
      </div>
    </section>
  );

  const boxTable = () => {
    const { counts, result } = view;
    const ratio = calc.mode === 'ratio';
    return (
      <section aria-label="박스 호수" style={{ ...card, marginBottom: '12px', overflowX: 'auto' }}>
        <h3 style={sectionTitle}>박스 호수표</h3>
        <table aria-label="박스 호수표" style={{ borderCollapse: 'collapse', minWidth: '760px' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>호수</th>
              <th style={th}>가로(cm)</th><th style={th}>세로(cm)</th><th style={th}>높이(cm)</th>
              <th style={th}>구성비(%)</th><th style={th}>수량(건)</th><th style={th}>박스 부피(㎥)</th><th style={th}>11톤 대수</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, i) => (
              <tr key={row.size_no} data-testid="dc-box-row">
                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{row.size_no}호</td>
                {DIM_KEYS.map((k) => <td key={k} style={td}>{cellInput(`specs.${i}.${k}`, `${i + 1}호 ${DIM_LABELS[k]}`)}</td>)}
                <td style={td}>{ratio ? cellInput(`ratio.mix.${i}`, `${i + 1}호 구성비(%)`) : fmtRatio(row.mixPct)}</td>
                <td style={td}>{ratio ? fmtInt(row.qty) : cellInput(`qty.boxes.${i}`, `${i + 1}호 수량(건)`)}</td>
                <td style={td}>{fmtVol2(row.volume)}</td>
                <td style={td}>{fmtTrucks(row.trucks)}</td>
              </tr>
            ))}
            <tr data-testid="dc-box-total" style={{ fontWeight: 600 }}>
              <td style={{ ...td, textAlign: 'left' }}>합계</td>
              <td style={td} colSpan={3} />
              <td style={td}>
                {fmtRatio(counts.mixSum)}
                {ratio && !isHundred(counts.mixSum) && (
                  <span data-testid="dc-mix-badge" title="구성비 합계가 100이 아닙니다 — 합계 대비 비율로 나눕니다" style={{ ...badge('warn'), marginLeft: '6px' }}>≠100</span>
                )}
              </td>
              <td style={td}>{fmtInt(counts.box)}</td>
              <td style={td}>{fmtVol2(result.boxVolume)}</td>
              <td style={td}>{fmtTrucks(result.boxTrucks)}</td>
            </tr>
          </tbody>
        </table>
      </section>
    );
  };

  const results = () => {
    const r = view.result;
    const items = [
      ['파우치', 'dc-pouch', `${fmtInt(r.pouch)}건`],
      ['박스', 'dc-box', `${fmtInt(r.box)}건`],
      ['마대', 'dc-sacks', `${fmtInt(r.sacks)}개`],
      ['일 필요 부피', 'dc-daily-m3', `${fmtVol1(r.dailyM3)}㎥`],
      ['11톤 1대 적재', 'dc-truck-load', `${fmtVol1(r.truckLoad)}㎥`],
      ['필요 대수', 'dc-need', `${fmtTrucks(r.need)}대`],
      ['배차(올림)', 'dc-dispatch', `${fmtInt(r.dispatch)}대`],
      ['마지막 차 적재율', 'dc-last', `${r.lastPct}%`],
      ['마대분', 'dc-sack-trucks', `${fmtTrucks(r.sackTrucks)}대`],
      ['박스분', 'dc-box-trucks', `${fmtTrucks(r.boxTrucks)}대`],
    ];
    return (
      <section aria-label="계산 결과" style={{ marginBottom: '16px' }}>
        <h3 style={{ ...sectionTitle, color: V.text }}>계산 결과</h3>
        <div data-testid="dc-subtitle" style={{ fontSize: '15px', fontWeight: 700, color: COLORS.primaryDark, marginBottom: '6px' }}>{subtitle(r)}</div>
        {view.alt && (
          <div style={{ marginBottom: '10px' }}>
            <span data-testid="dc-alt-sack" style={badge('default')}>{altSackText(view.alt)}</span>
          </div>
        )}
        {/* 🔴 컨테이너는 겉 div 에 건다 — 컨테이너 질의는 자식만 꾸민다(그리드 자신에 걸면 열 수가 안 바뀐다). */}
        <div style={{ containerType: 'inline-size', containerName: 'dc-metrics', marginTop: view.alt ? 0 : '10px' }}>
          <div className="gw-metrics dc-metrics" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', marginBottom: 0 }}>
            {items.map(([label, testId, value]) => (
              <div key={testId} className="gw-metric">
                <div style={{ fontSize: '12px', color: V.secondary }}>{label}</div>
                <div data-testid={testId} className="gw-metric-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const scenarioTable = () => (
    <section aria-label="시나리오" style={{ ...card, marginBottom: '12px', overflowX: 'auto' }}>
      <h3 style={sectionTitle}>시나리오 비교</h3>
      <table aria-label="시나리오 비교" style={{ borderCollapse: 'collapse', minWidth: '760px' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>시나리오</th>
            <th style={th}>파우치:박스</th><th style={th}>마대당</th><th style={th}>마대</th><th style={th}>1~5호 구성비</th>
            <th style={th}>일 필요 부피</th><th style={th}>11톤 대수</th><th style={th}>배차(올림)</th>
          </tr>
        </thead>
        <tbody>
          {view.rows.map((row) => (
            <tr key={row.key} data-testid={`dc-scenario-${row.key}`}>
              <th scope="row" style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{row.name}</th>
              <td style={td}>{`${fmtRatio(row.pouchPct)}:${fmtRatio(row.boxPct)}`}</td>
              <td style={td}>{fmtNum(row.perSack)}</td>
              <td style={td}>{fmtInt(row.sacks)}</td>
              <td style={td}>{row.mix.map(fmtRatio).join('/')}</td>
              <td style={td}>{`${fmtVol1(row.dailyM3)}㎥`}</td>
              <td style={td}>{fmtTrucks(row.need)}</td>
              <td style={td}>{`${fmtInt(row.dispatch)}대`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );

  const shareBlock = () => (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="primary" onClick={copyShare}>메신저 공유용 복사</Button>
        {shareFallback && (
          <span data-testid="dc-share-fallback" role="status" style={{ fontSize: '12px', color: COLORS.warn }}>선택됨 · Ctrl+C로 복사하세요</span>
        )}
      </div>
      {shareFallback && (
        <textarea ref={shareRef} data-testid="dc-share-text" aria-label="공유 문구" readOnly value={shareText(calc, presets)} rows={12}
          style={{ ...input, marginTop: '8px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical' }} />
      )}
    </div>
  );

  const logic = () => (
    <details style={{ ...card, marginBottom: '12px', fontSize: '12px', color: COLORS.textSub, lineHeight: 1.8 }}>
      <summary style={{ cursor: 'pointer', color: COLORS.text, fontWeight: 600 }}>계산 로직 보기</summary>
      <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
        <li>비율 입력: 파우치 = 총 출고 물량 × 파우치 비율(0~100%로 맞춤, 반올림) · 박스 = 총 출고 물량 − 파우치.</li>
        <li>호수별 박스 수 = 박스 × 그 호수 구성비 ÷ 구성비 합계(반올림). 반올림으로 생긴 차이는 구성비가 0보다 큰 마지막 호수에 더하거나 뺍니다. 구성비 합계가 100이 아니어도 합계 대비로 나눕니다.</li>
        <li>호수별 수량 입력: 입력한 수량을 그대로 쓰고, 구성비는 그 호수 수량 ÷ 박스 합계입니다. 박스 구성 프리셋을 누르면 지금 박스 합계를 그 구성비로 다시 나눕니다.</li>
        <li>마대 = 파우치 ÷ 마대당 파우치(올림).</li>
        <li>11톤 1대 적재 = 적재함 부피 × 차량 적재율 · 마대 1개 자리 = 1대 적재 ÷ 1대당 마대.</li>
        <li>박스 부피 = 가로 × 세로 × 높이 ÷ 1,000,000 × 수량(㎥).</li>
        <li>여유 계수 = 1 + 안전 여유율 · 마대분 = 마대 × 여유 계수 ÷ 출고 일수 ÷ 1대당 마대 · 박스분 = 박스 부피 합 × 여유 계수 ÷ 출고 일수 ÷ 1대 적재.</li>
        <li>일 필요 부피 = (마대 × 마대 1개 자리 + 박스 부피 합) × 여유 계수 ÷ 출고 일수.</li>
        <li>필요 대수 = 마대분 + 박스분 · 배차 = 필요 대수 올림 · 마지막 차 적재율 = (필요 대수 − (배차 − 1)) × 100(반올림 · 딱 떨어지면 100% · 필요 0대면 0%).</li>
        <li>입력 보정: 출고 일수는 정수로 반올림해 최소 1일, 마대당 파우치·11톤 1대당 마대는 최소 1, 차량 적재율은 1~100%, 안전 여유율은 최소 0%, 적재함 부피는 최소 0.1㎥로 맞춰 계산하고 칸 아래에 «1로 계산»처럼 안내합니다.</li>
        <li>시나리오는 지금의 총 물량·출고 일수·여유율·적재 기준·박스 규격에 계절·박스 구성 프리셋을 적용해 비교합니다.</li>
        <li>고친 계산기 값은 내 브라우저에 사용자별로 저장돼 새로고침해도 유지됩니다. «기본값으로 되돌리기»를 누르면 공용 기본값으로 돌아갑니다.</li>
      </ul>
    </details>
  );

  const ready = status === 'ready' && Boolean(view);

  return (
    <AppShell app="dispatch-calc">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: `var(--gw-bg, ${COLORS.bg})` }}>
        <Page>
          <style>{PAGE_CSS}</style>
          {/* h1 은 상태와 무관하게 늘 그린다 · 단추는 actions 가 아니라 머리 아래 줄(header()) */}
          <PageHeader icon={IconCalculator} tone="yellow" title="배차 계산기"
            subtitle={ready ? <span data-testid="dc-role-guide">{ROLE_GUIDE}</span> : null} />
          {status === 'loading' && <div role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</div>}
          {status === 'forbidden' && (
            <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText }}>배차 계산기 권한이 없습니다.</div>
          )}
          {status === 'error' && (
            <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span>공용 기본값을 불러오지 못했습니다.</span>
              <Button variant="secondary" onClick={loadAll}>다시 시도</Button>
            </div>
          )}
          {ready && (
            <>
              {header()}
              {changedBanner()}
              {panelOpen && server.can_edit && (
                <DispatchDefaultsPanel server={server} calcState={calc} onClose={() => setPanelOpen(false)} onReload={reloadServer} />
              )}
              {inputs()}
              {results()}
              {boxTable()}
              {scenarioTable()}
              {shareBlock()}
              {logic()}
            </>
          )}
        </Page>
      </main>
    </AppShell>
  );
}
